import axios from "axios";

import type {
  ISEBankIDAuthenticateResponse,
  ISEBankIDCollectParams,
  ISEBankIDCollectResponse,
} from "./types";

const BASE_URL = "/api/v1/se/bank-id";

const AUTHENTICATE_URL = `${BASE_URL}/authenticate`;
const COLLECT_URL = `${BASE_URL}/collect`;
const CANCEL_URL = `${BASE_URL}/cancel`;

/**
 * Collect is polled on an interval, and React Query hands a caller the request
 * already in flight rather than starting a second one — so one request left
 * hanging stalls the whole poll. Time out well above the observed worst case
 * instead, which surfaces as a retriable transport error.
 */
const REQUEST_TIMEOUT_MS = 10_000;

// Create axios instance without interceptors to avoid circular dependencies
const bankIDApiClient = axios.create({
  baseURL: import.meta.env.API_ROOT_URL,
  timeout: REQUEST_TIMEOUT_MS,
});

/**
 * Initiate BankID authentication
 * @param ssn - Personal number (only used for testing in non-production)
 */
export const authenticateViaBankID = async (ssn?: string) => {
  return bankIDApiClient.post<ISEBankIDAuthenticateResponse>(AUTHENTICATE_URL, {
    ssn: ssn ?? undefined,
  });
};

/**
 * Collect authentication status
 * This endpoint is polled until the user completes or cancels the authentication
 */
export const collectViaBankID = async (params: ISEBankIDCollectParams) => {
  return bankIDApiClient.post<ISEBankIDCollectResponse>(COLLECT_URL, params, {
    // ADMIN_WEB completes into a Django session rather than tokens, so the
    // response carries Set-Cookie — and a cross-origin response's cookies are
    // dropped unless the request itself was credentialed. Scoped per request so
    // customer/partner/lender traffic stays uncredentialed as before.
    withCredentials: params.authenticationContext === "ADMIN_WEB",
  });
};

/**
 * Cancel ongoing BankID authentication
 */
export const cancelViaBankID = async (orderRef: string) => {
  return bankIDApiClient.post(CANCEL_URL, { orderRef });
};
